import { api } from './apiClient';

export const cogsService = {
  // Get all COGS rules with filtering and pagination
  async getRules(params = {}) {
    try {
      const response = await api.get('/api/costing-rules', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get single COGS rule by ID
  async getRule(id) {
    try {
      const response = await api.get(`/api/costing-rules/${id}`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new COGS rule
  async createRule(ruleData) {
    try {
      const response = await api.post('/api/costing-rules', ruleData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Create and submit COGS rule for approval in one step
  async createAndSubmitRule(ruleData) {
    try {
      const response = await api.post('/api/costing-rules/submit', ruleData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Update existing COGS rule
  async updateRule(id, ruleData) {
    try {
      const response = await api.put(`/api/costing-rules/${id}`, ruleData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Create new version from existing COGS rule (edit-as-new)
  async createNewVersionFromExisting(id, ruleData) {
    try {
      const response = await api.post(`/api/costing-rules/${id}/edit-as-new`, ruleData);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },



  // Update costing rule description only (Attach URL)
  async updateDescription(id, description) {
    try {
      const response = await api.patch(`/api/costing-rules/${id}/description`, { description });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Get rule history
  async getRuleHistory(id) {
    try {
      const response = await api.get(`/api/costing-rules/${id}/history`);
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Submit rule for approval
  async submitForApproval(id, remarks = '') {
    try {
      const response = await api.post(`/api/approvals/${id}/submit`, { remarks });
      return response.data;
    } catch (error) {

      // Re-throw with more context if needed
      if (error.message && error.message.includes('No eligible approvers')) {
        throw new Error('No eligible approvers found for this entity. Please contact an administrator.');
      }

      if (error.message && error.message.includes('Database error')) {
        throw new Error('Database connection issue. Please try again later.');
      }

      throw error;
    }
  },

  // Approve rule
  async approveRule(id, remarks = '') {
    try {
      const response = await api.post(`/api/approvals/${id}/approve`, { remarks });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Reject rule
  async rejectRule(id, remarks) {
    try {
      const response = await api.post(`/api/approvals/${id}/reject`, { remarks });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get pending approvals
  async getPendingApprovals(params = {}) {
    try {
      const response = await api.get('/api/approvals/pending', { params });
      return response.data.data;
    } catch (error) {
      throw error;
    }
  },

  // Calculate fee preview
  async calculateFee(ruleData, amount) {
    try {
      // This would be a utility function to calculate fees client-side
      // or could be an API endpoint for complex calculations
      let fee = 0;
      
      if (ruleData.flatFee) {
        fee += parseFloat(ruleData.flatFee);
      }
      
      if (ruleData.percentageRate && amount) {
        fee += (parseFloat(ruleData.percentageRate) / 100) * amount;
      }
      
      // Apply minimum fee
      if (ruleData.minimumFee && fee < parseFloat(ruleData.minimumFee)) {
        fee = parseFloat(ruleData.minimumFee);
      }
      
      // Apply maximum fee
      if (ruleData.maximumFee && fee > parseFloat(ruleData.maximumFee)) {
        fee = parseFloat(ruleData.maximumFee);
      }
      
      return fee;
    } catch (error) {
      throw error;
    }
  },

  // Validate rule data
  validateRule(ruleData) {
    const errors = [];
    
    // Required fields
    if (!ruleData.entityId) errors.push('Entity is required');
    if (!ruleData.productId) errors.push('Product is required');
    if (!ruleData.schemeId) errors.push('Pricing scheme is required');
    if (!ruleData.feeCurrencyCode) errors.push('Currency is required');
    if (!ruleData.description || !ruleData.description.trim()) errors.push("Description is required - attach partner's contract link and other details about the cost");
    
    // At least one pricing method (unless using tiered pricing)
    const hasTieredPricing = ruleData.tieredPricing && (
      ruleData.tieredPricing.tier1FixedFee ||
      ruleData.tieredPricing.tier1MDR ||
      ruleData.tieredPricing.tier2FixedFee ||
      ruleData.tieredPricing.tier2MDR ||
      ruleData.tieredPricing.tier3FixedFee ||
      ruleData.tieredPricing.tier3MDR
    );

    const hasFlatFee = ruleData.flatFee !== null && ruleData.flatFee !== undefined && ruleData.flatFee !== '';
    const hasPercentageRate = ruleData.percentageRate !== null && ruleData.percentageRate !== undefined && ruleData.percentageRate !== '';

    if (!hasFlatFee && !hasPercentageRate && !hasTieredPricing) {
      errors.push('Either flat fee, percentage rate, or tiered pricing configuration is required');
    }

    // Validate numeric fields
    if (hasFlatFee && (isNaN(ruleData.flatFee) || ruleData.flatFee < 0)) {
      errors.push('Flat fee must be a valid non-negative number');
    }

    if (hasPercentageRate && (isNaN(ruleData.percentageRate) || ruleData.percentageRate < 0)) {
      errors.push('Percentage rate must be a valid non-negative number');
    }

    if (ruleData.minimumFee !== null && ruleData.minimumFee !== undefined && ruleData.minimumFee !== '' && (isNaN(ruleData.minimumFee) || ruleData.minimumFee < 0)) {
      errors.push('Minimum fee must be a valid non-negative number');
    }

    if (ruleData.maximumFee !== null && ruleData.maximumFee !== undefined && ruleData.maximumFee !== '' && (isNaN(ruleData.maximumFee) || ruleData.maximumFee < 0)) {
      errors.push('Maximum fee must be a valid non-negative number');
    }
    
    // Validate VAT rate when includeVAT is TRUE
    if (ruleData.includeVAT === 'TRUE') {
      if (ruleData.vatRate === null || ruleData.vatRate === undefined || ruleData.vatRate === '') {
        errors.push('VAT rate is required when Include VAT is TRUE');
      } else if (isNaN(ruleData.vatRate) || ruleData.vatRate < 0 || ruleData.vatRate > 100) {
        errors.push('VAT rate must be between 0 and 100');
      }
    }

    // Validate date ranges
    if (ruleData.effectiveStartDate && ruleData.effectiveEndDate) {
      const startDate = new Date(ruleData.effectiveStartDate);
      const endDate = new Date(ruleData.effectiveEndDate);
      
      if (endDate <= startDate) {
        errors.push('Effective end date must be after start date');
      }
    }
    
    // Validate minimum/maximum fee relationship
    const hasMinFee = ruleData.minimumFee !== null && ruleData.minimumFee !== undefined && ruleData.minimumFee !== '';
    const hasMaxFee = ruleData.maximumFee !== null && ruleData.maximumFee !== undefined && ruleData.maximumFee !== '';
    if (hasMinFee && hasMaxFee) {
      const minFee = parseFloat(ruleData.minimumFee);
      const maxFee = parseFloat(ruleData.maximumFee);
      
      if (minFee > maxFee) {
        errors.push('Minimum fee cannot be greater than maximum fee');
      }
    }
    
    return {
      isValid: errors.length === 0,
      errors,
    };
  },

  // Delete COGS rule
  async deleteRule(id, remarks = '') {
    try {
      const response = await api.delete(`/api/costing-rules/${id}`, {
        data: { remarks }
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Get rule status options
  getStatusOptions() {
    return [
      { value: 'DRAFT', label: 'Draft', color: 'default' },
      { value: 'PENDING', label: 'Pending Approval', color: 'processing' },
      { value: 'APPROVED', label: 'Approved', color: 'success' },
      { value: 'REJECTED', label: 'Rejected', color: 'error' },
      { value: 'PENDING_DELETION', label: 'Pending Deletion', color: 'warning' },
    ];
  },

  // Get refund policy options
  getRefundPolicyOptions() {
    return [
      { value: 'TRUE', label: 'Full Refund' },
      { value: 'FALSE', label: 'No Refund' },
      { value: 'PARTIAL', label: 'Partial Refund' },
    ];
  },

  // Get void policy options
  getVoidPolicyOptions() {
    return [
      { value: 'TRUE', label: 'Full Refund' },
      { value: 'FALSE', label: 'No Refund' },
      { value: 'FULL', label: 'Full Refund' },
    ];
  },

  // Format rule for display
  formatRuleForDisplay(rule) {
    return {
      ...rule,
      formattedFlatFee: rule.flatFee ? `${rule.currency?.symbol || ''}${rule.flatFee}` : null,
      formattedPercentageRate: rule.percentageRate ? `${rule.percentageRate}%` : null,
      formattedMinimumFee: rule.minimumFee ? `${rule.currency?.symbol || ''}${rule.minimumFee}` : null,
      formattedMaximumFee: rule.maximumFee ? `${rule.currency?.symbol || ''}${rule.maximumFee}` : null,
      statusColor: this.getStatusOptions().find(s => s.value === rule.status)?.color || 'default',
    };
  },
};
